import { type ClassValue, clsx } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * The design system defines custom typography utilities (`@utility text-*` in
 * theme.css: text-caption, text-label, text-body, text-overline, …). The default
 * tailwind-merge doesn't know these are FONT-SIZE utilities, so it classifies an
 * unknown `text-caption` as a text COLOR and, inside a single cn() call, treats
 * it as conflicting with a real color like `text-emerald-600` — silently dropping
 * the size (the element then inherits its parent's larger size). Registering them
 * in the `font-size` group fixes that: two sizes still conflict (last wins), but
 * a size and a color coexist. Keep this list in sync with the @utility text-*
 * blocks in src/styles/theme.css / tailwind.css.
 */
const CUSTOM_FONT_SIZE_SUFFIXES = [
  'hero',
  'display',
  'title-lg',
  'title-md',
  'title-sm',
  'title-xs',
  'title-compact',
  'body-lg',
  'body',
  'body-sm',
  'label',
  'label-compact',
  'caption',
  'caption-compact',
  'overline',
  'overline-compact',
  'section',
  '2xs',
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: CUSTOM_FONT_SIZE_SUFFIXES }],
    },
  },
});

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const buildQueryStringSSR = (params: Record<string, string | null>, currentSearchQuery?: string): string => {
  const queryParams = new URLSearchParams(currentSearchQuery);

  for (const [key, value] of Object.entries(params)) {
    if (value) {
      queryParams.set(key, value);
    } else {
      queryParams.delete(key);
    }
  }

  return `?${queryParams.toString()}`;
};

export const getRandomObject = (objectsArray: any[]): any | null => {
  if (!objectsArray) {
    return null;
  }

  const validObjects = objectsArray.filter(
    (obj) => obj !== null && obj !== undefined && !(Array.isArray(obj) && obj.length === 0) && !(typeof obj === "object" && Object.keys(obj).length === 0)
  );

  return validObjects.length > 0
    ? validObjects[Math.floor(Math.random() * validObjects.length)]
    : null;
};